import React, { forwardRef, useLayoutEffect, useRef } from "react";
import styles from "../Textarea/Textarea.module.css";
import { clsx } from "../../utils/clsx";
import { useComponentDefaults } from "../../Provider";

const TextareaAutosize = forwardRef((props, ref) => {
  const merged = useComponentDefaults("Textarea", props, {}, { demoteThemeKeys: ['color'] });
  const {
    className,
    error,
    hint,
    variant = "default",
    shape = "default",
    color = "default",
    minRows = 2,
    maxRows,
    onChange,
    style,
    value,
    defaultValue,
    ...rest
  } = merged;

  const innerRef = useRef(null);
  const setRef = (node) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  const resize = () => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = "auto";
    const computed = typeof window !== "undefined" ? window.getComputedStyle(el) : null;
    const rawLine = computed ? parseFloat(computed.lineHeight || "0") : 0;
    const fallbackLine = computed ? parseFloat(computed.fontSize || "0") * 1.2 : 0;
    const lineHeight = rawLine || fallbackLine || 20;
    const padding = computed ? parseFloat(computed.paddingTop || "0") + parseFloat(computed.paddingBottom || "0") : 0;
    const contentHeight = el.scrollHeight;
    let nextHeight = contentHeight;
    if (maxRows) {
      const maxHeight = lineHeight * maxRows + padding;
      if (maxHeight) nextHeight = Math.min(nextHeight, maxHeight);
    }
    if (minRows) {
      const minHeight = lineHeight * minRows + padding;
      if (minHeight) nextHeight = Math.max(nextHeight, minHeight);
    }
    el.style.height = `${nextHeight}px`;
  };

  useLayoutEffect(() => {
    resize();
  }, [value, defaultValue, minRows, maxRows]);

  const handleChange = (event) => {
    if (typeof onChange === "function") onChange(event);
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(resize);
    } else {
      resize();
    }
  };

  const colorClass = color !== "default" ? styles[`color${color[0].toUpperCase()}${color.slice(1)}`] : undefined;

  const controlProps = {};
  if (value !== undefined) controlProps.value = value;
  if (value === undefined && defaultValue !== undefined) controlProps.defaultValue = defaultValue;

  return (
    <div className={styles.wrap}>
      <textarea
        {...rest}
        {...controlProps}
        ref={setRef}
        rows={minRows}
        className={clsx(
          styles.area,
          variant === "filled" && styles.variantFilled,
          variant === "borderless" && styles.variantBorderless,
          shape === "round" && styles.areaRound,
          colorClass,
          className
        )}
        aria-invalid={!!error || undefined}
        style={style}
        onChange={handleChange}
      />
      {error ? <div className={styles.error} role="alert">{error}</div> : hint ? <div className={styles.hint}>{hint}</div> : null}
    </div>
  );
});

TextareaAutosize.displayName = "TextareaAutosize";

export default TextareaAutosize;
